import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';

// API/Components
import axiosInstance from '../components/axios';
import useHotkeyHandlers from '../components/hooks';
import UnitDischargeSnapshotReport from '../components/unit_discharge_snapshot_report';
import UnitDischargeSnapshotGraph from '../components/unit_discharge_snapshot_graph';
import { formatDateTime } from '../components/enums';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import IconButton from '@mui/material/IconButton';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { styled } from '@mui/material/styles';

const DataSwitch = styled(Switch)(({ theme }) => ({
	width: 30,
	height: 18,
	padding: 0,
	display: 'flex',
	'& .MuiSwitch-switchBase': {
		padding: 2,
		'&.Mui-checked': {
			transform: 'translateX(14px)',
			color: '#fff',
			'& + .MuiSwitch-track': {
				opacity: 1,
				backgroundColor: '#B1282E',
			},
		},
	},
	'& .MuiSwitch-thumb': {
		boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
		width: 12,
		height: 14,
		borderRadius: 2,
		transition: theme.transitions.create(['width'], {
			duration: 200,
		}),
	},
	'& .MuiSwitch-track': {
		borderRadius: 4,
		opacity: 1,
		backgroundColor: '#0F1820',
		boxSizing: 'border-box',
	},
}));


// Unit Snapshot sub-page rendering (Location Name, Title, String Selection, Measurement Selection)
export default function UnitSnapshot() {
	const { locationNum, eventDateTime } = useParams();
	
	// State Management
	const [locationInfo, setLocationInfo] = useState({
		locationName: '',
		numStrings: 0,
		numUnits: 0,
	});
	
	const [selectedString, setSelectedString] = useState(0);
	
	const [dischargeMeasList, setDischargeMeasList] = useState([]);
	const [selectedMeasIndex, setSelectedMeasIndex] = useState(Number(0));
	const [measArrowDisabled, setMeasArrowDisabled] = useState({
		back: true,
		forward: true,
	});
	
	const [snapshotData, setSnapshotData] = useState({});
	
	const [isLoading, setIsLoading] = useState(true);
	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	const selectedMeasIndexRef = useRef();
	const graphTypeRef = useRef();
	
	// Handle the current selectedMeasIndex for hotkey nav
	useEffect(() => {
		selectedMeasIndexRef.current = selectedMeasIndex;
	}, [selectedMeasIndex]);

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle the enabling/disabling of the measurement navigation buttons
	useEffect(() => {
		if (dischargeMeasList !== null && dischargeMeasList.length > 0) {
			setMeasArrowDisabled({
				back: selectedMeasIndex === 0,
				forward: selectedMeasIndex === Number(dischargeMeasList.length - 1),
			});
		}
	}, [selectedMeasIndex]);

	// Handle keypresses for measurement and graph navigation
	useHotkeyHandlers([
		{
			key: 'PageUp',
			down: (e) => {
				e.preventDefault();  // Prevent default scrolling behavior
				if (dischargeMeasList.length !== 0 && selectedMeasIndexRef.current !== 0) {
					setSelectedMeasIndex(Number(selectedMeasIndexRef.current - 1));
				}
			},
			up: () => {},
		},
		{
			key: 'PageDown',
			down: (e) => {
				e.preventDefault();
				if (dischargeMeasList.length !== 0 && selectedMeasIndexRef.current !== (dischargeMeasList.length - 1)) {
					setSelectedMeasIndex(Number(selectedMeasIndexRef.current + 1));
				}
			},
			up: () => {},
		},
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Temperature
						setGraphType(1);
						break;
					case 1:  // Temperature -> Voltage
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);
	
	// Get information about the location id specified (name, size, num of temps)
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/size/`).then(response => {
			const locationInfo = response.data;
			setLocationInfo({
				locationName: locationInfo.location_name,
				serialNumber: locationInfo.serial_number,
				numStrings: locationInfo.num_strings,
				numUnits: locationInfo.num_units_per_string,
			});
		});
	}, [locationNum]);
	
	// Get the list of measurement ids and datetimes for the specified location id
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/snapshot/`).then(response => {
			setIsLoading(true);
			
			const measurementList = response.data;
			setDischargeMeasList(measurementList);
			
			if (measurementList.length !== 0) {
				setSelectedMeasIndex(Number(0));
				setMeasArrowDisabled({
					forward: measurementList.length === 1,
					back: true
				});
			}
			setIsLoading(false);
		});
	}, [locationNum, eventDateTime]);
	
	// Get all snapshot data for the specified measurement id
	useEffect(() => {
		if (dischargeMeasList.length && selectedMeasIndex >= 0) {
			setIsLoading(true);
			let discharge_id = dischargeMeasList[Number(selectedMeasIndex)].discharge_id;
			axiosInstance.get(`/loc/${locationNum}/discharges/${eventDateTime}/snapshot/${discharge_id}`).then(response => {
				const snapshotData = response.data;
				setSnapshotData(snapshotData);
			});
			setIsLoading(false);
		}
		setIsLoading(false);
	}, [locationNum, eventDateTime, selectedMeasIndex, dischargeMeasList]);

		
	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleStringChange = (event) => {
		setSelectedString(Number(event.target.value));
	}
	
	const handleMeasurementChange = (event) => {
		setSelectedMeasIndex(Number(event.target.value));
	}
	
	const handleMeasurementChangeBack = () => {
		if (dischargeMeasList.length !== 0 && selectedMeasIndex !== 0) {
			setSelectedMeasIndex(Number(selectedMeasIndex - 1));
		}
	}
	
	const handleMeasurementChangeForward = () => {
		if (dischargeMeasList.length !== 0 && selectedMeasIndex !== (dischargeMeasList.length - 1)) {
			setSelectedMeasIndex(Number(selectedMeasIndex + 1));
		}
	}
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
		
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.locationName } (SN {locationInfo.serialNumber})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							Unit Discharge Snapshot
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									<option key={1} value={1}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="string-select" style={{fontFamily:"Saira, sans-serif"}}>String</label>
							<select
								id="string-select"
								value={selectedString}
								label="String"
								onChange={handleStringChange}
								sx= {{ maxHeight: 500 }}
								>
								<option key={0} value={0}>All Strings</option>
								{ Array.from({length: locationInfo.numStrings}, (_, i) => i + 1).map((stringNum) => (
									<option key={stringNum} value={stringNum}>String {stringNum}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<IconButton
							variant="contained"
							onClick={handleMeasurementChangeBack}
							disabled={measArrowDisabled.back}
							sx={{
								ml: 1,
								mr: 0,
								pl: 2,
								height: 25,
								width: 25,
								borderRadius: '5px',
								border: '1px solid',
								align: 'center',
								bgcolor: '#B1282E',
								'&:hover': {
									bgcolor: "#B1282E77"
								},
							}}
						>
							<ArrowBackIosIcon sx={{ fontSize: 15, color: "#FFFFFF" }} align='center' />
						</IconButton>
					</Grid>
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 160 }}>
							<label id="measurement-select" style={{fontFamily:"Saira, sans-serif"}}>Measurement</label>
							<select
								id="measurement-select"
								value={selectedMeasIndex}
								label="Measurement"
								onChange={handleMeasurementChange}
								sx= {{ maxHeight: 500 }}
							>
								{dischargeMeasList.map((dischargeMeas, index) => (
									<option
										key={dischargeMeas.discharge_id}
										value={index}
									>
										{formatDateTime(dischargeMeas.datetime)} ({dischargeMeas.elapsed_time})
									</option>
								))}
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<IconButton
							variant='contained'
							onClick={handleMeasurementChangeForward}
							disabled={measArrowDisabled.forward}
							sx={{
								ml: 0,
								mr: 1,
								pl: 1,
								height: 25,
								width: 25,
								borderRadius: '5px',
								border: '1px solid',
								align: 'center',
								bgcolor: '#B1282E',
								'&:hover': {
									bgcolor: "#B1282E77"
								},
							}}
						>
							<ArrowForwardIosIcon sx={{ fontSize: 15, color: "#FFFFFF" }} align='center' />
						</IconButton>
					</Grid>
					<Grid item>
						<Tooltip title="Data display type">
							<Stack direction="row" spacing={1} alignItems="center">
								<Typography fontFamily='Saira, sans-serif'>Report</Typography>
									<DataSwitch checked={graphDisplay} onChange={handleViewChange} disabled={isLoading}/>
								<Typography fontFamily='Saira, sans-serif'>Graph</Typography>
							</Stack>
						</Tooltip>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<UnitDischargeSnapshotGraph
					snapshotData={snapshotData}
					selectedString={selectedString}
					numUnits={locationInfo.numUnits}
					graphType={graphType}
					loading={isLoading}
				/>
			:
				<UnitDischargeSnapshotReport
					snapshotData={snapshotData}
					selectedString={selectedString}
					loading={isLoading}
				/>
			}
		</Paper>
	);
}